import { create } from "zustand";

/** Latest widget payload shown in the UI (SQL-backed or markdown). */
export type DashboardAssistantWidgetOutput = {
  loading: boolean;
  error_message: string | null;
  columns: string[];
  rows: Record<string, unknown>[];
  /** Widget is hidden until required parameters are set. */
  gated?: boolean;
  /** Markdown widget body */
  markdown?: string;
};

/** One widget row for assistant / chat context (no SQL text). */
export type DashboardAssistantWidgetSummary = {
  id: string;
  type: string;
  title: string;
  has_sql: boolean;
  position: number;
  /** Populated asynchronously to match on-screen query results. */
  output?: DashboardAssistantWidgetOutput;
};

/** Parameter metadata for the model (no secrets). */
export type DashboardAssistantParameterSummary = {
  id: string;
  type: string;
  label: string;
};

export type DashboardAssistantContextState = {
  dashboard_id: string;
  route_path: string;
  title: string;
  description: string | null;
  is_public: boolean;
  parameters: DashboardAssistantParameterSummary[];
  parameter_values: Record<string, unknown>;
  resolved_sql_params: Record<string, unknown>;
  widgets: DashboardAssistantWidgetSummary[];
};

type DashboardAssistantContextStore = {
  context: DashboardAssistantContextState | null;
  setDashboardContext: (context: DashboardAssistantContextState | null) => void;
  patchDashboardWidgetOutput: (widgetId: string, output: DashboardAssistantWidgetOutput) => void;
};

export const useDashboardAssistantContextStore = create<DashboardAssistantContextStore>((set) => ({
  context: null,
  setDashboardContext: (context) => set({ context }),
  patchDashboardWidgetOutput: (widgetId, output) =>
    set((state) => {
      if (!state.context) return state;
      const widgets = state.context.widgets.map((widget) =>
        widget.id === widgetId ? { ...widget, output } : widget,
      );
      return { context: { ...state.context, widgets } };
    }),
}));
